import React from 'react';
import type { LanguageOption, PendingLanguageSwitch } from '../sdk/language-preference';
import type { SupportedLanguage } from '../sdk/ui-strings';
import { getString } from '../sdk/ui-strings';

interface LanguageSelectorProps {
  languages: LanguageOption[];
  value: SupportedLanguage;
  pending?: PendingLanguageSwitch | null;
  error?: string | null;
  onChange: (language: SupportedLanguage) => void;
  appearance?: 'light' | 'dark';
  disabled?: boolean;
}

/**
 * Compact by design: language is conversation chrome, not a second settings
 * panel. A native select keeps keyboard, screen-reader and mobile picker
 * behaviour correct inside both the light website card and dark demo overlay.
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  languages,
  value,
  pending,
  error,
  onChange,
  appearance = 'light',
  disabled = false,
}) => {
  if (languages.length <= 1) return null;

  const dark = appearance === 'dark';
  const foreground = dark ? '#F5F0EB' : '#3F3A36';
  const muted = dark ? 'rgba(245,240,235,0.62)' : '#79716B';
  const border = dark ? 'rgba(245,240,235,0.18)' : '#E5E0DC';
  const background = dark ? 'rgba(245,240,235,0.07)' : '#FBF9F8';
  const isPending = !!pending;

  return (
    <div
      data-floe-language-selector
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 3,
        minWidth: 0,
        flexShrink: 0,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans Devanagari", sans-serif',
      }}
    >
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          height: 30,
          padding: '0 8px',
          border: `1px solid ${border}`,
          borderRadius: 999,
          color: foreground,
          background,
          opacity: disabled || isPending ? 0.65 : 1,
        }}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ flexShrink: 0 }}
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
        </svg>
        <span
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {getString('language.label')}
        </span>
        <select
          aria-label={getString('language.label')}
          value={value}
          disabled={disabled || isPending}
          onChange={(event) => onChange(event.target.value as SupportedLanguage)}
          style={{
            maxWidth: 112,
            border: 'none',
            outline: 'none',
            appearance: 'auto',
            background: 'transparent',
            color: foreground,
            font: 'inherit',
            fontSize: 11.5,
            fontWeight: 600,
            cursor: disabled || isPending ? 'wait' : 'pointer',
          }}
        >
          {languages.map((language) => (
            <option key={language.code} value={language.code} style={{ color: '#1A1816' }}>
              {language.nativeLabel}
            </option>
          ))}
        </select>
      </label>
      {isPending && (
        <span role="status" aria-live="polite" style={{ fontSize: 10, color: muted }}>
          {getString('language.switching')}
        </span>
      )}
      {!isPending && error && (
        <span
          role="alert"
          title={error}
          style={{ maxWidth: 150, fontSize: 10, lineHeight: 1.25, color: '#D95D52' }}
        >
          {error}
        </span>
      )}
    </div>
  );
};
